// "Show first" row on the search page (DEV-50739 SP-001).
//
// One toggle pill per supplier+branch in the basket. Ticking a pill marks
// that branch as prioritised; SP-003 sorts matching products to the top.
// Only BRANCH_PRIORITY_MAX_VISIBLE_PILLS branches render inline — the rest
// sit behind a "+N more" button that opens the SP-002 bottom sheet.
//
// Which two are visible: ticked branches first, then the rest, each group
// in basket order — so a branch ticked from the sheet is never left
// hidden behind "+N more". The order is fixed ("pinned") between
// re-orders: it is rebuilt only when the row first appears, when the
// basket's branches change, or when the sheet is applied — never on a
// pill tap, so pills don't jump under the engineer's finger.
// Renders nothing while the basket is loading or when it is empty.
import { useRef, useState } from 'react'
import { Check } from 'lucide-react'
import { BranchPrioritySheet } from './BranchPrioritySheet'
import { useBranchPriority } from '../../hooks/useBranchPriority'
import type { BasketBranch } from '../../hooks/useBranchPriority'
import type { AtBranchSource } from '../../services/engineerSession'
import { BRANCH_PRIORITY_MAX_VISIBLE_PILLS } from '../../constants/branchPriority'

// How the location's counter is described. The device knows where the
// engineer is; a typed postcode only knows the area, so it must not claim
// otherwise.
const HERE_LABELS: Record<AtBranchSource, string> = {
  device: 'where you are',
  postcode: 'near your postcode',
}

// Ticked branches first, then unticked; basket order within each group.
function orderTickedFirst(branches: BasketBranch[], tickedKeys: string[]): string[] {
  const ticked = branches.filter((branch) => tickedKeys.includes(branch.key))
  const unticked = branches.filter((branch) => !tickedKeys.includes(branch.key))
  return [...ticked, ...unticked].map((branch) => branch.key)
}

interface PinnedOrder {
  // Identifies the basket state (and store readiness) the order was built
  // for; a different signature means "rebuild".
  signature: string
  keys: string[]
}

export interface BranchPriorityPillsProps {
  // Fired after any tick change (pill tap or sheet Apply). Search uses it
  // to jump back to page 1 so the re-split never lands on an empty page.
  onSelectionChange?: () => void
  // Fired after any change (pill tap or sheet Apply) with every branch
  // ticked afterwards. Not fired when the change leaves nothing ticked.
  // Search reports it to analytics.
  onSelectionApplied?: (selectedBranches: BasketBranch[]) => void
}

export function BranchPriorityPills({
  onSelectionChange,
  onSelectionApplied,
}: BranchPriorityPillsProps) {
  const { isReady, basketBranches, selectedKeys, toggleKey, applyKeys } =
    useBranchPriority()
  const [isSheetOpen, setIsSheetOpen] = useState(false)
  // Focus returns to "+N more" when the sheet closes so keyboard / screen
  // reader users land back where they left.
  const moreButtonRef = useRef<HTMLButtonElement>(null)

  // Pinned display order (see header). Rebuilt during render when the
  // basket's branches change or the store becomes ready — the React
  // "derive state from a previous render" pattern, so no effect and no
  // flash of the stale order.
  const basketSignature = `${isReady ? 'ready' : 'loading'}|${basketBranches
    .map((branch) => branch.key)
    .join('|')}`
  const [pinnedOrder, setPinnedOrder] = useState<PinnedOrder | null>(null)
  if (pinnedOrder === null || pinnedOrder.signature !== basketSignature) {
    setPinnedOrder({
      signature: basketSignature,
      keys: orderTickedFirst(basketBranches, selectedKeys),
    })
  }

  if (!isReady || basketBranches.length === 0) return null

  const closeSheet = () => {
    setIsSheetOpen(false)
    moreButtonRef.current?.focus()
  }

  const branchByKey = new Map(basketBranches.map((branch) => [branch.key, branch]))
  const orderedKeys =
    pinnedOrder !== null && pinnedOrder.signature === basketSignature
      ? pinnedOrder.keys
      : orderTickedFirst(basketBranches, selectedKeys)
  const orderedBranches = orderedKeys
    .map((key) => branchByKey.get(key))
    .filter((branch): branch is BasketBranch => branch !== undefined)
  const visibleBranches = orderedBranches.slice(0, BRANCH_PRIORITY_MAX_VISIBLE_PILLS)
  const hiddenBranches = orderedBranches.slice(BRANCH_PRIORITY_MAX_VISIBLE_PILLS)
  const hiddenSelectedCount = hiddenBranches.filter((branch) =>
    selectedKeys.includes(branch.key),
  ).length

  return (
    <section aria-label="Show suppliers and branches first" className="mb-4">
      <p className="mb-1.5 text-sm font-semibold text-gray-900">Show first</p>
      <div className="flex items-stretch gap-1.5">
        {visibleBranches.map((branch) => {
          const checked = selectedKeys.includes(branch.key)
          return (
            <button
              key={branch.key}
              type="button"
              aria-pressed={checked}
              title={`${branch.supplierName} · ${branch.branchName}${
                branch.here ? ` · ${HERE_LABELS[branch.here]}` : ''
              }`}
              onClick={() => {
                toggleKey(branch.key)
                const nextKeys = checked
                  ? selectedKeys.filter((key) => key !== branch.key)
                  : [...selectedKeys, branch.key]
                if (nextKeys.length > 0) {
                  onSelectionApplied?.(
                    basketBranches.filter((candidate) => nextKeys.includes(candidate.key)),
                  )
                }
                onSelectionChange?.()
              }}
              // Phones (< sm): two-line pill capped at half the row so two
              // fit — supplier on line one, branch on line two, truncated
              // with an ellipsis when long (full name on the title and in
              // the "+N more" sheet). From sm up: the original inline
              // "Supplier · Branch" pill at its natural width.
              // One size everywhere: a 32px visible pill inside a 44px tap
              // area (the transparent vertical margin keeps the touch target
              // at the app's minimum). Phones: each pill is as wide as its
              // text and only shrinks (truncating its branch) when the row
              // is tight — so a short pill never leaves empty space while a
              // long one is cut. Capped at 60% of the row so a long name
              // still leaves room for its neighbour. Supplier stacks over
              // branch. sm+: natural width, "Supplier · Branch" inline.
              // Unticked: white fill, 1.5px brand border, brand supplier
              // name, grey branch name, brand tick. Ticked: solid brand fill,
              // names and tick in the tenant's brand text colour (the same
              // one primary buttons use, so a light brand stays readable).
              className={`relative inline-flex min-h-9 min-w-[85px] max-w-[60%] flex-initial cursor-pointer items-center gap-1.5 rounded-full border-[1.5px] border-brand-primary px-[9px] py-[3px] text-left sm:px-2.5 sm:py-1 transition-colors before:absolute before:-inset-y-1 before:inset-x-0 before:content-[''] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2 active:scale-[0.98] sm:max-w-none sm:flex-none ${
                checked
                  ? 'bg-brand-primary text-[var(--brand-primary-text)]'
                  : 'bg-white text-brand-primary'
              }`}
            >
              {/* Tick always present so the pill reads as tickable and its
                  width doesn't shift on toggle. */}
              <Check
                className={`h-3 w-3 shrink-0 ${checked ? 'text-[var(--brand-primary-text)]' : 'text-brand-primary'}`}
                strokeWidth={3}
                aria-hidden="true"
              />
              <span className="flex min-w-0 flex-col leading-[0.95rem] sm:flex-row sm:flex-wrap sm:items-baseline sm:gap-x-1 sm:leading-4">
                <span className="truncate text-xs font-semibold sm:overflow-visible sm:whitespace-normal sm:text-[13px]">
                  {branch.supplierName}
                </span>
                <span
                  className={`truncate text-[11px] sm:overflow-visible sm:whitespace-normal ${
                    checked ? 'text-[var(--brand-primary-text)] opacity-90' : 'text-gray-600'
                  }`}
                >
                  <span className="hidden sm:inline">· </span>
                  {branch.branchName}
                  {/* AC4: the counter's pill says how it was chosen, without
                      spending pill width on it. Only a device fix justifies
                      claiming the engineer is standing there. */}
                  {branch.here && (
                    <span className="sr-only">, {HERE_LABELS[branch.here]}</span>
                  )}
                </span>
              </span>
            </button>
          )
        })}
        {hiddenBranches.length > 0 ? (
          <button
            ref={moreButtonRef}
            type="button"
            onClick={() => setIsSheetOpen(true)}
            aria-haspopup="dialog"
            aria-expanded={isSheetOpen}
            aria-label={`${hiddenBranches.length} more supplier branches, ${hiddenSelectedCount} selected`}
            className={`relative flex min-h-9 min-w-[85px] shrink-0 cursor-pointer items-center justify-center gap-1 rounded-full border-[1.5px] border-brand-primary px-2.5 text-[11px] font-semibold text-brand-primary transition-colors before:absolute before:-inset-y-1 before:inset-x-0 before:content-[''] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2 active:scale-[0.98] ${
              hiddenSelectedCount > 0 ? 'bg-brand-primary-tint' : 'bg-white'
            }`}
          >
            {hiddenSelectedCount > 0 ? (
              <Check className="h-3 w-3" strokeWidth={3} aria-hidden="true" />
            ) : null}
            +{hiddenBranches.length} more
          </button>
        ) : null}
      </div>
      {hiddenBranches.length > 0 ? (
        <BranchPrioritySheet
          open={isSheetOpen}
          onClose={closeSheet}
          branches={basketBranches}
          initialSelectedKeys={selectedKeys}
          onApply={(keys) => {
            applyKeys(keys)
            const appliedBranches = basketBranches.filter((branch) => keys.includes(branch.key))
            const changed =
              appliedBranches.length !== selectedKeys.length ||
              appliedBranches.some((branch) => !selectedKeys.includes(branch.key))
            if (changed && appliedBranches.length > 0) onSelectionApplied?.(appliedBranches)
            // Applying from the sheet is the one tick change that re-orders
            // the row, so a newly ticked hidden branch comes into view.
            setPinnedOrder({
              signature: basketSignature,
              keys: orderTickedFirst(basketBranches, keys),
            })
            onSelectionChange?.()
          }}
        />
      ) : null}
    </section>
  )
}
